import React, { useState, useEffect } from 'react';
import { FileText, Printer, Download, User, Search } from 'lucide-react';
import { generateDoctorOpinionPDF, generateDoctorOpinionPrintHTML } from '../../utils/doctorOpinionGenerator';
import { opinionsAPI } from '../../../../api';
import { patientAPI } from '../../../../api/patient';
import { useToast } from '../../../../hooks/useToast';
import { useAuth } from '../../../../state/auth/useAuth';
import { CARD_STYLES, BUTTON_STYLES, INPUT_STYLES } from '../../../receptionist-dashboard/utils/constants';

const DoctorOpinionTab = ({ patientId }) => {
  // Core form state (minimal required fields)
  const [formData, setFormData] = useState({
    patientId: patientId || '',
    assignedDoctorId: '',
    speciality: '',
    toDoctorName: '',
    notes: ''
  });

  const { user } = useAuth();

  // UI/aux state
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientQuery, setPatientQuery] = useState('');
  const [patientResults, setPatientResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showError, showSuccess, showInfo } = useToast();

  // Set current doctor (logged-in user) as author
  useEffect(() => {
    const id = user?._id || user?.id;
    if (id) {
      setFormData(prev => ({ 
        ...prev, 
        assignedDoctorId: id,
        speciality: prev.speciality || user?.specialty || user?.speciality || ''
      }));
    }
  }, [user]);

  // Update patientId when prop changes
  useEffect(() => {
    if (patientId) {
      setFormData(prev => ({ ...prev, patientId }));
      // Try to fetch and show patient info when patientId is provided from parent
      (async () => {
        try {
          const res = await patientAPI.getById(patientId);
          const p = res?.data || res?.patient || res;
          if (p) setSelectedPatient(p);
        } catch (e) {
          // Non-blocking
        }
      })();
    }
  }, [patientId]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async () => {
    if (!formData.patientId) {
      showError('Patient is required');
      return;
    }

    if (!formData.notes.trim()) {
      showError('Doctor opinion/notes are required');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        patientId: formData.patientId,
        assignedDoctorId: formData.assignedDoctorId,
        speciality: formData.speciality || '',
        toDoctorName: formData.toDoctorName || '',
        notes: formData.notes
      };

      const response = await opinionsAPI.create(payload);
      
      if (response.success) {
        showSuccess('Doctor opinion saved successfully');
        // Reset minimal fields
        setFormData(prev => ({ ...prev, notes: '' }));
      }
    } catch (error) {
      console.error('Error saving opinion:', error);
      showError('Failed to save doctor opinion');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Simple debounced patient search
  useEffect(() => {
    const q = String(patientQuery || '').trim();
    if (!q) { setPatientResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await patientAPI.searchPatients(q);
        const pts = res?.data?.patients || [];
        setPatientResults(pts);
      } catch (e) {
        setPatientResults([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [patientQuery]);

  if (loading) {
    return (
      <div className={`${CARD_STYLES.default} p-6`}>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500 font-medium">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${CARD_STYLES.default} p-6 space-y-6`}>
      <div className="flex items-center gap-2 mb-4">
        <FileText className="w-6 h-6 text-[#2D869C]" />
        <h2 className="text-xl font-bold text-[#838383]">Doctor Opinion Form</h2>
      </div>

      {/* Patient selection */}
      {!patientId ? (
        <div>
          <label className="block text-sm font-semibold text-[#838383] mb-2">
            Select Patient <span className="text-[#E99797]">*</span>
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={patientQuery}
              onChange={(e) => setPatientQuery(e.target.value)}
              className={`${INPUT_STYLES.default} pl-10`}
              placeholder="Search by name, mobile, or ID..."
            />
          </div>
          {patientResults.length > 0 && (
            <div className="mt-2 max-h-52 overflow-auto border-2 border-[#9DC9D4]/30 rounded-lg shadow-md">
              {patientResults.map(p => (
                <button
                  key={p._id || p.id}
                  type="button"
                  onClick={() => {
                    setSelectedPatient(p);
                    setFormData(prev => ({ ...prev, patientId: p._id || p.id }));
                    setPatientResults([]);
                    setPatientQuery('');
                  }}
                  className="w-full text-left px-4 py-3 text-sm font-medium text-[#838383] hover:bg-[#9DC9D4]/10 border-b last:border-b-0 transition-colors duration-200"
                >
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-[#2D869C]" />
                    <span>{(p.firstName || p.name || '') + ' ' + (p.lastName || '')}</span>
                    <span className="text-xs text-gray-500">• {(p.mobile || p.phone || '')}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
          {selectedPatient && (
            <div className={`mt-2 p-3 ${CARD_STYLES.highlight}`}>
              <p className="text-sm font-medium text-[#838383]">
                <span className="text-[#2D869C]">Selected:</span> {(selectedPatient.firstName || selectedPatient.name || '') + ' ' + (selectedPatient.lastName || '')}
                <span className="text-xs text-gray-500 ml-2">(ID: {selectedPatient._id || selectedPatient.id})</span>
              </p>
            </div>
          )}
        </div>
      ) : (
        selectedPatient && (
          <div className={`p-4 ${CARD_STYLES.highlight}`}>
            <div className="flex items-center gap-2">
              <User className="w-5 h-5 text-[#2D869C]" />
              <p className="text-sm font-semibold text-[#838383]">
                Patient: {(selectedPatient.firstName || selectedPatient.name || '') + ' ' + (selectedPatient.lastName || '')}
              </p>
            </div>
          </div>
        )
      )}

      {/* Doctor info (current user) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <label className="block text-sm font-semibold text-[#838383] mb-2">Doctor</label>
          <input
            type="text"
            value={user?.name || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'Unknown'}
            readOnly
            className={INPUT_STYLES.disabled}
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-[#838383] mb-2">Speciality</label>
          <input
            type="text"
            value={formData.speciality}
            readOnly
            className={INPUT_STYLES.disabled}
            placeholder="Auto-filled"
          />
        </div>
      </div>

      {/* To Doctor Name (optional) */}
      <div>
        <label className="block text-sm font-semibold text-[#838383] mb-2">To Doctor (optional)</label>
        <input
          type="text"
          name="toDoctorName"
          value={formData.toDoctorName}
          onChange={handleInputChange}
          className={INPUT_STYLES.default}
          placeholder="e.g., Dr. Johnson"
        />
      </div>

      {/* Opinion / Notes */}
      <div>
        <label className="block text-sm font-semibold text-[#838383] mb-2">
          Doctor Opinion / Notes <span className="text-[#E99797]">*</span>
        </label>
        <textarea
          name="notes"
          value={formData.notes}
          onChange={handleInputChange}
          className={`${INPUT_STYLES.default} h-48 resize-none`}
          placeholder="Enter doctor's opinion..."
          required
        />
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3 pt-4 border-t-2 border-[#9DC9D4]/30">
        <button
          onClick={handleSubmit}
          className={`${BUTTON_STYLES.primary} disabled:opacity-50 disabled:cursor-not-allowed`}
          disabled={
            isSubmitting ||
            !formData.notes.trim() ||
            !formData.patientId ||
            !formData.assignedDoctorId
          }
        >
          {isSubmitting ? 'Saving...' : 'Save Opinion'}
        </button>
        <button
          type="button"
          onClick={() => {
            if (!formData.notes.trim() || !formData.patientId) return;
            const html = generateDoctorOpinionPrintHTML(formData, selectedPatient || {}, user || {});
            const w = window.open('', '_blank', 'width=800,height=600');
            if (!w) { showError('Please allow pop-ups to print'); return; }
            w.document.write(html);
            w.document.close();
            w.onload = () => {
              setTimeout(() => { w.focus(); w.print(); }, 200);
            };
            w.onafterprint = () => { try { w.close(); } catch (_) {} };
          }}
          className={`${BUTTON_STYLES.secondary} flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
          disabled={!formData.notes.trim() || !formData.patientId}
        >
          <Printer className="w-4 h-4" />
          Print
        </button>
        <button
          type="button"
          onClick={async () => {
            if (!formData.notes.trim() || !formData.patientId) return;
            const res = await generateDoctorOpinionPDF(formData, selectedPatient || {}, user || {});
            if (res?.success) {
              showInfo('PDF download started');
            } else {
              showError(res?.error || 'Failed to generate PDF');
            }
          }}
          className={`${BUTTON_STYLES.success} flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
          disabled={!formData.notes.trim() || !formData.patientId}
        >
          <Download className="w-4 h-4" />
          Download PDF
        </button>
      </div>
    </div>
  );
};

export default DoctorOpinionTab;
